// Visões da agenda: dia, semana e mês (VF-2) — puras.
//
// A consulta é a MESMA das três — `dataHora` num intervalo, um campo só,
// coberto pelo índice automático. O que muda é o recorte. Nenhum índice novo,
// nenhuma função nova.
//
// ★ A VISÃO SEMANAL É POR TURNO, e não uma grade horária.
//
// O dia útil tem 19 vagas. Dezenove linhas em sete colunas não cabem na tela —
// e "mostrar só os horários com vaga" NÃO resolve: com uma vaga por horário e a
// agenda quase vazia, quase todos estão livres, e voltariam as 19 linhas.
//
// Turno resolve porque responde a pergunta que a semana faz — *onde tem espaço
// esta semana?* — em duas linhas por dia. As 19 linhas continuam existindo, no
// DIA, que é onde a pergunta é outra: *quem vem hoje?*

import { chaveDia, ocupacaoDoDia } from '@/agenda/grade';

export const VISOES = [
  { id: 'dia', label: 'Dia' },
  { id: 'semana', label: 'Semana' },
  { id: 'mes', label: 'Mês' },
];

/**
 * Os turnos.
 *
 * A fronteira é 13:00 porque é onde a grade do dia útil recomeça depois do
 * almoço — não é uma regra nova, é a mesma lista de horários lida em dois
 * pedaços.
 */
export const TURNOS = [
  { id: 'manha', label: 'Manhã', de: '00:00', ate: '12:59' },
  { id: 'tarde', label: 'Tarde', de: '13:00', ate: '23:59' },
];

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const aoMeioDia = (d) => {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return x;
};

/** Segunda a domingo da semana que contém `data`. */
export function limitesDaSemana(data) {
  const d = paraData(data);
  if (!d) return null;
  const inicio = aoMeioDia(d);
  // getDay(): 0 = domingo. Segunda é o começo, então domingo recua 6 dias.
  const recuo = (inicio.getDay() + 6) % 7;
  inicio.setDate(inicio.getDate() - recuo);
  const fim = new Date(inicio);
  fim.setDate(fim.getDate() + 6);
  return { inicio, fim };
}

/** Primeiro e último dia do mês de `data`, ao meio-dia. */
export function limitesDoMesVisao(data) {
  const d = paraData(data);
  if (!d) return null;
  return {
    inicio: aoMeioDia(new Date(d.getFullYear(), d.getMonth(), 1)),
    fim: aoMeioDia(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

/** Os sete dias da semana de `data`. */
export function diasDaSemana(data) {
  const janela = limitesDaSemana(data);
  if (!janela) return [];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(janela.inicio);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/**
 * As semanas do mês, cada uma com sete dias.
 *
 * Inclui os dias das semanas de borda que caem em outro mês — o calendário
 * precisa deles para as colunas ficarem alinhadas —, marcados com `doMes:
 * false` para a tela apagá-los.
 */
export function semanasDoMes(data) {
  const mes = limitesDoMesVisao(data);
  if (!mes) return [];
  const semanas = [];
  let cursor = limitesDaSemana(mes.inicio).inicio;
  while (cursor <= mes.fim) {
    semanas.push(
      diasDaSemana(cursor).map((d) => ({ data: d, doMes: d.getMonth() === mes.inicio.getMonth() })),
    );
    const proximo = new Date(cursor);
    proximo.setDate(proximo.getDate() + 7);
    cursor = proximo;
  }
  return semanas;
}

/** Agendamentos agrupados por dia ('AAAA-MM-DD'). */
export function agruparPorDia(agendamentos = []) {
  const mapa = new Map();
  for (const a of agendamentos ?? []) {
    const chave = chaveDia(a?.dataHora);
    if (!chave) continue;
    if (!mapa.has(chave)) mapa.set(chave, []);
    mapa.get(chave).push(a);
  }
  return mapa;
}

/** Contagem por status, para a célula do mês. */
export function contagemPorStatus(lista = []) {
  const saida = { agendado: 0, compareceu: 0, faltou: 0, cancelado: 0 };
  for (const a of lista ?? []) {
    const s = a?.status ?? 'agendado';
    if (s in saida) saida[s] += 1;
  }
  return saida;
}

/**
 * O resumo de um dia por TURNO — o que a visão semanal desenha.
 *
 * ★ TRÊS ESTADOS DIFERENTES, e a tela não pode confundi-los:
 *
 *   `fechado`      a clínica não atende avaliação neste dia (com `motivo`,
 *                  quando é bloqueio explícito). NÃO é "cheio".
 *   `semGrade`     o turno não tem NENHUMA vaga configurada — a tarde de um dia
 *                  que só atende de manhã. NÃO é "livre" nem "cheio".
 *   livres > 0     há vaga, e `primeiraLivre` diz qual.
 *
 * Confundir `semGrade` com livre ofereceria um horário que não existe;
 * confundir com cheio faria a pessoa procurar outro dia sem precisar.
 */
export function resumoDoDia(config, data, agendamentos = []) {
  const ocupacao = ocupacaoDoDia(config, data, agendamentos);
  const turnos = TURNOS.map((t) => {
    const linhas = ocupacao.linhas.filter((l) => l.hora >= t.de && l.hora <= t.ate);
    const vagas = linhas.reduce((n, l) => n + l.vagas, 0);
    const livres = linhas.reduce((n, l) => n + l.livres, 0);
    return {
      id: t.id,
      label: t.label,
      // ★ Sem NENHUM horário configurado neste turno.
      semGrade: linhas.length === 0,
      vagas,
      livres,
      cheio: linhas.length > 0 && livres === 0,
      primeiraLivre: linhas.find((l) => !l.cheio)?.hora ?? null,
    };
  });

  return {
    chave: chaveDia(data),
    data,
    fechado: ocupacao.fechado,
    motivo: ocupacao.motivo,
    vagas: ocupacao.vagas,
    livres: ocupacao.livres,
    turnos,
    total: (agruparPorDia(agendamentos).get(chaveDia(data)) ?? []).length,
  };
}

/** A semana inteira, dia a dia. */
export function resumoDaSemana(config, data, agendamentos = []) {
  return diasDaSemana(data).map((d) => resumoDoDia(config, d, agendamentos));
}

/** Rótulo curto de navegação, por visão. */
export function rotuloDaVisao(visao, data) {
  const d = paraData(data) ?? new Date();
  if (visao === 'semana') {
    const { inicio, fim } = limitesDaSemana(d);
    const curto = (x) => `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}`;
    return `${curto(inicio)} a ${curto(fim)}`;
  }
  if (visao === 'mes') {
    return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }
  return chaveDia(d);
}

/** Avança ou recua uma unidade da visão. */
export function navegar(visao, data, passo) {
  const d = aoMeioDia(paraData(data) ?? new Date());
  if (visao === 'semana') d.setDate(d.getDate() + 7 * passo);
  else if (visao === 'mes') d.setMonth(d.getMonth() + passo);
  else d.setDate(d.getDate() + passo);
  return d;
}
